import React, { useState, useEffect, useRef } from 'react';
import api from '../api/api';
import { formatDateTime, getOrderStatusLabel, getPaymentMethodLabel } from '../utils/dashboardTranslations';

function ActiveOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acceptingOrders, setAcceptingOrders] = useState({});
    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
    const [newOrdersCount, setNewOrdersCount] = useState(0);
    const intervalRef = useRef(null);
    const previousOrderCountRef = useRef(0);

    useEffect(() => {
        fetchOrders();
        
        // Set up polling for live updates every 30 seconds
        intervalRef.current = setInterval(() => {
            console.log('🔄 Active Orders: Background refresh for new orders...');
            fetchOrders(true); // true for background fetch
        }, 30000);

        // Cleanup interval on component unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await api.get('/vendor/orders/admin-view');
            setOrders(res.data || []);
        } catch {
            alert('فشل تحميل الطلبات النشطة');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptOrder = async (orderId) => {
        setAcceptingOrders(prev => ({ ...prev, [orderId]: true }));
        try {
            // Set estimated delivery time to 1 hour from now
            const estimatedDeliveryTime = new Date();
            estimatedDeliveryTime.setHours(estimatedDeliveryTime.getHours() + 1);
            
            const response = await api.put(`/Order/accept/${orderId}`, 
                `"${estimatedDeliveryTime.toISOString()}"`, 
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (response.status === 200) {
                alert('تم قبول الطلب بنجاح');
                // Update the order status in the local state
                setOrders(prevOrders => 
                    prevOrders.map(order => 
                        order.id === orderId 
                            ? { ...order, status: 'Accepted', estimatedDeliveryTime: estimatedDeliveryTime }
                            : order
                    )
                );
            }
        } catch (error) {
            console.error('Error accepting order:', error);
            alert('فشل في قبول الطلب');
        } finally {
            setAcceptingOrders(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const userOrders = orders.filter(order => order.user);

    return (
        <div>
            <h4 className="mb-3">الطلبات النشطة</h4>
            {loading ? <div>جاري التحميل...</div> : (
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>رقم الطلب</th>
                            <th>الحالة</th>
                            <th>اسم العميل</th>
                            <th>البائع</th>
                            <th>السائق</th>
                            <th>رقم السائق</th>
                            <th>تاريخ الإنشاء</th>
                            <th>طريقة الدفع</th>
                            <th>ملاحظات</th>
                            <th>الملخص المالي</th>
                            <th>تفاصيل المنتجات</th>
                            <th>الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {userOrders.length === 0 ? (
                            <tr><td colSpan="12">لا توجد طلبات نشطة من المستخدمين</td></tr>
                        ) : (
                            userOrders.map(order => (
                                <tr key={order.id}>
                                    <td>{order.id}</td>
                                    <td><span className={`badge bg-${order.status === 'Delivered' ? 'success' : order.status === 'Pending' ? 'warning' : 'info'}`}>{getOrderStatusLabel(order.status)}</span></td>
                                    <td>{order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}` : '—'}</td>
                                    <td>{order.vendor ? order.vendor.name : '—'}</td>
                                    <td>{order.driver ? order.driver.name : '—'}</td>
                                    <td>{order.driver ? order.driver.phone : '—'}</td>
                                    <td>{formatDateTime(order.createdAt)}</td>
                                    <td>{getPaymentMethodLabel(order.paymentMethod)}</td>
                                    <td>{order.notes || order.Notes || '—'}</td>
                                    <td>
                                        <div className="small text-start">
                                            <div>الإجمالي: {order.totalAmount ?? 0}</div>
                                            <div>خصم النقاط: -{order.amountPaidWithPoints ?? 0}</div>
                                            <div>التوصيل: {order.deliveryFee ?? 0}</div>
                                            <div className="fw-bold text-success">
                                                المطلوب تحصيله: {order.amountPaidWithMoney ?? 0}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {order.orderDetails && order.orderDetails.length > 0 ? (
                                            <ul style={{ paddingLeft: 18 }}>
                                                {order.orderDetails.map((item, idx) => (
                                                    <li key={idx}>
                                                        {item.productName || '-'} - الكمية: {item.quantity ?? '-'} - السعر: {item.price ?? '-'}
                                                        {' - اللون: '}{item.colorName || item.ColorName || '-'}
                                                        {' - الحجم: '}{item.sizeName || item.SizeName || '-'}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        {order.status === 'Pending' ? (
                                            <button 
                                                className="btn btn-success btn-sm"
                                                onClick={() => handleAcceptOrder(order.id)}
                                                disabled={acceptingOrders[order.id]}
                                            >
                                                {acceptingOrders[order.id] ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        جاري القبول...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-check me-1"></i>
                                                        قبول الطلب
                                                    </>
                                                )}
                                            </button>
                                        ) : order.status === 'Accepted' ? (
                                            <span className="badge bg-success">تم القبول</span>
                                        ) : order.status === 'Delivered' ? (
                                            <span className="badge bg-info">تم التسليم</span>
                                        ) : (
                                            <span className="badge bg-secondary">{getOrderStatusLabel(order.status)}</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default ActiveOrders;
